import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { DbProduct, getProduct, sendMessage, getMessages, Message, markMessagesAsRead, deleteConversation } from '../../utils/api';
import { MessageCircle, Send, X, Trash2, Share2 } from 'lucide-react';
import ImageGallery from '../../components/gallery/ImageGallery';
import { useAuth } from '../auth/AuthContext';
import SellerRating from './SellerRating';
import ProductCard from './ProductCard';
import Seo from '../common/Seo';
import { breadcrumbJsonLd } from '../../utils/jsonLd';
import WhatsAppShareButton from '../common/WhatsAppShareButton';

const ProductDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const chatUserId = queryParams.get('user_id');

  const [product, setProduct] = useState<DbProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  // Chat automatisch öffnen, wenn ?chat=true in der URL ist
  const [showChat, setShowChat] = useState(location.search.includes('chat=true'));
  const [message, setMessage] = useState<string>('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [deletingChat, setDeletingChat] = useState(false);
  const [showLoginInfo, setShowLoginInfo] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState<DbProduct[]>([]);
  const [shareCopied, setShareCopied] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Dieser Effect aktualisiert den showLoginInfo-State, wenn sich der user-State ändert
  useEffect(() => {
    setShowLoginInfo(!user);
  }, [user]);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        if (!id) return;
        const response = await getProduct(id);
        console.log('Product response:', response);
        if (response.success && response.data) {
          setProduct(response.data);
        } else {
          setError(response.message);
        }
      } catch (err) {
        setError('Fehler beim Laden des Produkts');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  // Ähnliche Produkte laden
  useEffect(() => {
    if (!product) return;
    fetch(`/api/products.php?category=${encodeURIComponent(product.category)}&limit=4`)
      .then(r => r.json())
      .then(res => {
        if (res.success && res.data) {
          setRelatedProducts(res.data.filter((p: DbProduct) => p.id !== product.id).slice(0, 4));
        }
      })
      .catch(() => {});
  }, [product]);

  // Scroll to bottom of messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Load messages when chat is opened
  useEffect(() => {
    if (showChat && product && user) {
      const loadMessages = async () => {
        try {
          setLoadingMessages(true);
          console.log('Lade Nachrichten für Produkt ID:', product.id);
          
          let response;
          if (chatUserId) {
            console.log('Lade Nachrichten mit spezifischem Benutzer:', chatUserId);
            response = await getMessages(parseInt(id!), parseInt(chatUserId));
          } else {
            response = await getMessages(parseInt(id!));
          }
          
          console.log('Nachrichten-Response:', response);
          
          if (response.success && response.data) {
            console.log('Nachrichten gefunden:', response.data.length);
            console.log('Nachrichten Daten:', response.data);
            setMessages(response.data);
            
            // Mark messages as read
            console.log('Markiere Nachrichten als gelesen');
            let markResponse;
            if (chatUserId) {
              markResponse = await markMessagesAsRead(parseInt(id!), parseInt(chatUserId));
            } else {
              markResponse = await markMessagesAsRead(parseInt(id!));
            }
            console.log('Markiere als gelesen Response:', markResponse);
          } else {
            console.error('Fehler beim Laden der Nachrichten:', response.message);
          }
        } catch (err) {
          console.error('Error loading messages:', err);
        } finally {
          setLoadingMessages(false);
        }
      };
      
      loadMessages();
    }
  }, [showChat, product, user, id, chatUserId]);

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: product?.title, url });
    } else {
      navigator.clipboard.writeText(url).then(() => {
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
      });
    }
  };

  const handleChatClick = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    setShowChat(!showChat);
    
    // URL aktualisieren, wenn Chat geöffnet/geschlossen wird
    if (!showChat) {
      navigate(`/products/${id}?chat=true`, { replace: true });
    } else {
      navigate(`/products/${id}`, { replace: true });
    }
  };

  const handleDeleteChat = async () => {
    if (!product || !window.confirm('Möchtest du diese Konversation wirklich löschen? Das kann nicht rückgängig gemacht werden.')) {
      return;
    }
    
    setDeletingChat(true);
    try {
      let response;
      if (chatUserId) {
        response = await deleteConversation(product.id, parseInt(chatUserId));
      } else {
        response = await deleteConversation(product.id);
      }
      
      if (response.success) {
        setMessages([]);
        alert('Konversation erfolgreich gelöscht');
        // Optional: Chat schließen
        setShowChat(false);
      } else {
        alert('Fehler beim Löschen der Konversation: ' + response.message);
      }
    } catch (err) {
      console.error('Error deleting chat:', err);
      alert('Fehler beim Löschen der Konversation');
    } finally {
      setDeletingChat(false);
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim() || !user || !product) {
      console.error('Nachricht, User oder Produkt fehlt:', { 
        message: !!message.trim(), 
        user: !!user, 
        product: !!product 
      });
      return;
    }
    
    // Finden Sie den anderen Benutzer in den bisherigen Nachrichten
    let receiver_id;
    
    if (chatUserId) {
      // Wenn chatUserId gesetzt ist, senden wir an diesen Benutzer
      receiver_id = parseInt(chatUserId);
    } else if (Number(user.id) === Number(product.user_id)) {
      // Wenn Sie der Verkäufer sind und kein chatUserId vorhanden ist,
      // versuchen Sie, den Empfänger aus den Nachrichten zu ermitteln
      const otherUserMessages = messages.filter(msg => Number(msg.sender_id) !== Number(user.id));
      if (otherUserMessages.length > 0) {
        receiver_id = otherUserMessages[otherUserMessages.length - 1].sender_id;
      } else {
        // Fallback, sollte eigentlich nicht vorkommen
        alert('Kein Empfänger gefunden. Bitte starte die Konversation über die Nachrichtenübersicht.');
        return;
      }
    } else {
      // Standard: an Verkäufer senden
      receiver_id = product.user_id;
    }
    
    console.log('Sende Nachricht an:', receiver_id);
    console.log('Produkt-ID:', product.id);
    console.log('Nachrichteninhalt:', message);
    
    setSendingMessage(true);
    try {
      const messageData = {
        receiver_id: receiver_id,
        product_id: product.id,
        message: message
      };
      console.log('MessageData für API:', messageData);
      
      const response = await sendMessage(messageData);
      console.log('API-Antwort:', response);
      
      if (response.success && response.data) {
        console.log('Nachricht erfolgreich gesendet:', response.data);
        setMessages(prevMessages => [...prevMessages, response.data]);
        setMessage('');
      } else {
        console.error('Fehler bei der API-Antwort:', response);
      }
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSendingMessage(false);
    }
  };

  if (loading) {
    return <div className="container mx-auto px-4 py-8">Lädt...</div>;
  }

  if (error || !product) {
    return (
      <div className="container mx-auto px-4 py-8">
        {/* Nicht (mehr) vorhandene Produkte nicht indexieren -> kein Soft-404 */}
        <Seo title="Produkt nicht gefunden | SportyReplay" noindex />
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error || 'Produkt nicht gefunden'}
        </div>
      </div>
    );
  }

  // Der Gesprächspartner ist entweder der in der URL angegebene Benutzer oder der erste Benutzer in den Nachrichten
  let chatPartnerName: string = product.seller_name || "Verkäufer";
  
  // Fügen Sie hier einen Null-Check für user hinzu
  if (user && Number(user.id) === Number(product.user_id)) {
    if (chatUserId) {
      // Finde den Namen des Chat-Partners in den Nachrichten
      const chatPartnerMessage = messages.find(msg => 
        Number(msg.sender_id) === Number(chatUserId) || 
        Number(msg.receiver_id) === Number(chatUserId)
      );
      
      if (chatPartnerMessage) {
        chatPartnerName = Number(chatPartnerMessage.sender_id) === Number(chatUserId) 
          ? (chatPartnerMessage.sender_name || "Unbekannt")
          : (chatPartnerMessage.receiver_name || "Unbekannt");
      }
    } else if (messages.length > 0) {
      // Finde einen anderen Benutzer in den Nachrichten
      // Hier muss auch ein Null-Check für user.id eingefügt werden
      const otherUserMessage = messages.find(msg => 
        user && Number(msg.sender_id) !== Number(user.id)
      );
      
      if (otherUserMessage) {
        chatPartnerName = otherUserMessage.sender_name || "Unbekannt";
      }
    }
  }

  const formattedDate = new Date(product.created_at).toLocaleDateString('de-DE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const allImages = Array.from(new Set([product.image_url, ...(product.images || [])])).filter(Boolean);

  const seoDescription = (product.description || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 155) ||
    `${product.title} gebraucht aus dem Verein ${product.club} – günstig bei SportyReplay kaufen.`;

  const productUrl = `https://sportyreplay.de/products/${product.id}`;
  const toAbsoluteUrl = (u: string) => (u?.startsWith('http') ? u : `https://sportyreplay.de${u}`);
  const productJsonLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.title,
    description: seoDescription,
    image: allImages.map(toAbsoluteUrl),
    category: product.category,
    ...(product.product_type ? { additionalType: product.product_type } : {}),
    ...(product.size ? { size: String(product.size).trim() } : {}),
    offers: {
      '@type': 'Offer',
      price: Number(product.price),
      priceCurrency: 'EUR',
      itemCondition: 'https://schema.org/UsedCondition',
      availability: product.is_archived ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock',
      url: productUrl,
      // Kein seller-Feld: seller_name kann eine E-Mail sein -> keine PII ins indexierbare JSON-LD
    },
  };

  // Startseite > Kategorie > Produkt
  const breadcrumbLd = breadcrumbJsonLd([
    { name: 'Startseite', path: '/' },
    ...(product.category
      ? [{ name: product.category, path: `/kategorie/${encodeURIComponent(product.category)}` }]
      : []),
    { name: product.title, path: `/products/${product.id}` },
  ]);

  return (
    <div className="container mx-auto px-4 py-8">
      <Seo
        title={`${product.title} – ${Number(product.price)} € gebraucht | SportyReplay`}
        description={seoDescription}
        path={`/products/${product.id}`}
        image={product.image_url}
        type="product"
        jsonLd={[productJsonLd, breadcrumbLd]}
      />
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-lg shadow-xl overflow-hidden">
          <div className="flex flex-col lg:flex-row">
            {/* Bild-Sektion */}
            <div className="lg:w-1/2">
              <ImageGallery images={allImages} title={product.title} />
            </div>

            {/* Info-Sektion */}
            <div className="lg:w-1/2 p-8">
              <div className="mb-6">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h1 className="text-3xl font-bold text-gray-800">
                    {product.title}
                  </h1>
                  <div className="flex-shrink-0 flex items-center gap-2">
                    <WhatsAppShareButton
                      text={`${product.title} für ${Number(product.price).toFixed(2)} € – gebraucht auf SportyReplay`}
                      url={productUrl}
                    />
                    <button
                      onClick={handleShare}
                      className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
                      title="Teilen"
                    >
                      <Share2 size={18} />
                    </button>
                  </div>
                </div>
                {shareCopied && (
                  <p className="text-green-600 text-xs mb-2">Link kopiert!</p>
                )}
                <p className="text-2xl font-bold text-orange-500 mb-4">
                  {Number(product.price).toFixed(2)} €
                </p>
                <div className="border-t border-b py-4 my-4">
                  <div className="flex flex-wrap gap-4">
                    <div>
                      <p className="text-gray-600 text-sm">Größe</p>
                      <p className="font-semibold">{product.size}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 text-sm">Kategorie</p>
                      <p className="font-semibold">{product.category}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 text-sm">Eingestellt am</p>
                      <p className="font-semibold">{formattedDate}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Verkäufer-Info */}
              <div className="mb-6">
                <h2 className="text-xl font-semibold mb-2">Verkäufer</h2>
                <Link to={`/seller/${product.user_id}`} className="block bg-gray-50 hover:bg-orange-50 transition-colors p-4 rounded-lg group">
                  <p className="font-medium group-hover:text-orange-600 transition-colors">{product.seller_name}</p>
                  <p className="text-gray-600">{product.seller_club}</p>
                  <p className="text-xs text-orange-500 mt-1 group-hover:underline">Alle Angebote ansehen →</p>
                </Link>
                <SellerRating sellerId={product.user_id} sellerName={product.seller_name || 'Verkäufer'} />
              </div>

              {/* Beschreibung */}
              <div className="mb-6">
                <h2 className="text-xl font-semibold mb-2">Beschreibung</h2>
                <p className="text-gray-700 whitespace-pre-wrap">
                  {product.description}
                </p>
              </div>

              {/* Chat-Button Bereich */}
              {/* Nur anzeigen, wenn nicht eigenes Produkt oder nicht eingeloggt */}
              {(!user || (user && user.id !== product.user_id)) && (
                <div className="mb-6">
                  <button
                    onClick={handleChatClick}
                    className="w-full bg-orange-500 text-white py-3 px-6 rounded-lg hover:bg-orange-600 transition flex items-center justify-center gap-2"
                  >
                    <MessageCircle size={20} />
                    {showChat ? 'Chat schließen' : 'Verkäufer kontaktieren'}
                  </button>
                  
                  {/* Info-Hinweis für nicht eingeloggte Benutzer - jetzt mit eigenem State */}
                  {showLoginInfo && (
                    <div className="mt-2 flex items-start text-xs sm:text-sm bg-blue-50 text-blue-700 p-2 sm:p-3 rounded-lg border border-blue-200">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <span className="flex-1">Bitte logg dich ein, um den Verkäufer zu kontaktieren.</span>
                    </div>
                  )}
                </div>
              )}
              
              {/* Eingebetteter Chat-Bereich */}
              {showChat && user && (
                <div className="mt-6 bg-white rounded-2xl shadow-lg p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold text-gray-800">Chat mit {chatPartnerName}</h3>
                    <div className="flex items-center space-x-2">
                      {/* Löschbutton */}
                      <button
                        onClick={handleDeleteChat}
                        disabled={deletingChat}
                        className="text-red-500 hover:text-red-700 bg-red-100 p-1 rounded-full"
                        title="Konversation löschen"
                      >
                        <Trash2 size={16} />
                      </button>
                      {/* Schließbutton */}
                      <button
                        onClick={() => setShowChat(false)}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>

                  <div className="h-64 overflow-y-auto border rounded-lg p-3 mb-4 bg-gray-50">
                    {loadingMessages ? (
                      <p className="text-gray-500 text-center py-4">
                        Nachrichten werden geladen...
                      </p>
                    ) : messages.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">
                        Noch keine Nachrichten. Starte die Konversation!
                      </p>
                    ) : (
                      <div className="space-y-4">
                        {messages.map(msg => {
                          const isSent = user && Number(msg.sender_id) === Number(user.id);
                          const time = new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                          return isSent ? (
                            <div key={msg.id} className="flex justify-end">
                              <div className="flex items-start space-x-3 max-w-[70%] flex-row-reverse space-x-reverse">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold shadow-lg flex-shrink-0">
                                  M
                                </div>
                                <div>
                                  <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-2xl rounded-tr-none px-5 py-3 shadow-md">
                                    <p>{msg.message}</p>
                                  </div>
                                  <span className="text-xs text-gray-500 mt-1 block mr-2 text-right">{time}</span>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div key={msg.id} className="flex justify-start">
                              <div className="flex items-start space-x-3 max-w-[70%]">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-bold shadow-lg flex-shrink-0">
                                  {(msg.sender_name || 'V')[0].toUpperCase()}
                                </div>
                                <div>
                                  <div className="bg-white rounded-2xl rounded-tl-none px-5 py-3 shadow-md">
                                    <p className="text-gray-800">{msg.message}</p>
                                  </div>
                                  <span className="text-xs text-gray-500 mt-1 block ml-2">{time}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        <div ref={messagesEndRef} />
                      </div>
                    )}
                  </div>

                  {/* Schnellnachrichten */}
                  <div className="flex flex-wrap gap-2 mb-2">
                    {[
                      'Ist der Artikel noch verfügbar?',
                      `Würdest du ${Math.round(Number(product.price) * 0.8)} € akzeptieren?`,
                      'Kann ich das Produkt besichtigen?'
                    ].map(q => (
                      <button
                        key={q}
                        onClick={() => setMessage(q)}
                        className="text-xs bg-gray-100 hover:bg-orange-50 hover:text-orange-600 text-gray-600 px-2 py-1 rounded-full transition-colors border border-gray-200"
                      >
                        {q}
                      </button>
                    ))}
                  </div>

                  <div className="flex">
                    <input
                      type="text"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && !sendingMessage && handleSendMessage()}
                      className="flex-1 border rounded-l-lg p-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="Deine Nachricht..."
                      disabled={sendingMessage || loadingMessages}
                    />
                    <button
                      onClick={handleSendMessage}
                      disabled={sendingMessage || loadingMessages}
                      className="bg-orange-500 text-white px-4 py-2 rounded-r-lg hover:bg-orange-600 transition disabled:opacity-50"
                    >
                      <Send size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Ähnliche Produkte */}
      {relatedProducts.length > 0 && (
        <div className="max-w-6xl mx-auto mt-10">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Ähnliche Produkte</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {relatedProducts.map(p => <ProductCard key={p.id} {...p} />)}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;